import React, { useCallback, useState } from 'react'
import { Card, Collapse, Spinner } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { getUserDisplayName } from '../../../../../apps/professional-app/fa/common/aifeatures/utils'
import { TestResultDisplay } from '../TestResultDisplay'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { IntegrationSettings } from '../../AdminSettings/schema'
import { useFormContext } from 'react-hook-form'
import { useLazyGetIntegrationUsersQuery } from '../../../../../store/integrations/integrations'
import { User } from '../../../../../models/User'
import { ContactDropdown } from '../../../../MeetingSummary/CRM/ContactDropdown'
import '../MappingTester.scss'

export interface ContactMappingTesterProps {
  serviceId: IntegrationServiceId
}

interface SelectedContactData {
  externalId: string
  fullName: string
  firstName: string
  lastName: string
  email: string
}

export const ContactMappingTester: React.FC<ContactMappingTesterProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const {
    formState: { isDirty, isValid },
  } = useFormContext<IntegrationSettings>()

  const isFormUnsaved = isDirty || !isValid
  const integrationName = getIntegrationDisplayName(serviceId)
  const [isOpen, setIsOpen] = useState(false)
  const [isTestRunning, setIsTestRunning] = useState(false)
  const [selectedContactData, setSelectedContactData] = useState<SelectedContactData | null>(null)
  const [rawResult, setRawResult] = useState<unknown | null>(null)
  const [fetchedUsers, setFetchedUsers] = useState<User[]>([])

  const [triggerGetContacts, { isSuccess: contactsSuccess, isError: contactsError }] = useLazyGetIntegrationUsersQuery()

  const handleRunTest = async () => {
    setIsOpen(true)
    setIsTestRunning(true)
    setSelectedContactData(null)
    try {
      const result = await triggerGetContacts({ accountId, connector: serviceId }).unwrap()
      setRawResult(result)
      if (result?.data) {
        setFetchedUsers(result.data)
      }
    } catch (err) {
      setRawResult(err)
    } finally {
      setIsTestRunning(false)
    }
  }

  const handleContactSelected = useCallback(
    (option: SelectOption) => {
      const user = fetchedUsers.find((u) => u.integrations?.some((i) => i.serviceId === serviceId && i.externalId === option.id))
      setSelectedContactData({
        externalId: option.id,
        fullName: user ? getUserDisplayName(user) : option.label,
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        email: user?.email ?? '',
      })
      if (user) setRawResult(user)
    },
    [fetchedUsers, serviceId],
  )

  return (
    <Card className='zocks-border-0.75 border-1 shadow-sm border-dark'>
      <Card.Body className='p-4'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <i className='bi bi-sliders2 fs-5'></i>
              <h6 className='fw-bold mb-0'>Mapping</h6>
            </div>
            <p className='text-secondary mb-0 small'>Preview individual contact data and verify Field-Level Contact Updates are synchronizing as configured.</p>
          </div>
          <Tooltip id='contact-run-test-tooltip' title={isFormUnsaved ? 'Save your changes before running a test' : undefined}>
            <Button
              type='button'
              variant='primaryGhost'
              className='d-flex align-items-center gap-2 fw-semibold flex-shrink-0'
              onClick={handleRunTest}
              disabled={isTestRunning || isFormUnsaved}
            >
              {isTestRunning ? <Spinner animation='border' size='sm' className='me-2' /> : <i className='bi bi-play-fill' />}
              Run Test
            </Button>
          </Tooltip>
        </div>

        <Collapse in={isOpen}>
          <div>
            <TestResultDisplay
              isLoading={isTestRunning}
              hasResult={contactsSuccess || contactsError}
              isSuccess={contactsSuccess}
              isError={contactsError}
              integrationName={integrationName}
              rawResult={rawResult}
              disableToggle={contactsSuccess && !selectedContactData}
              toggleShowLabel='Show Field-Level Contact Update Data'
              toggleHideLabel='Hide Field-Level Contact Update Data'
              errorContent={<p className='mb-0 text-dark ps-4'>Failed to fetch contacts from {integrationName}. Please verify your integration settings.</p>}
              successContent={
                <>
                  <div className='mb-3'>
                    <ContactDropdown serviceId={serviceId} onContactOptionSelected={handleContactSelected} />
                  </div>
                  {selectedContactData && (
                    <div className='mb-3'>
                      <h6 className='fw-bold text-secondary text-uppercase small mb-3'>Contact Details</h6>
                      <div className='row g-2'>
                        <div className='col-6'>
                          <label className='form-label small text-secondary mb-1'>Full name</label>
                          <input type='text' className='form-control' value={selectedContactData.fullName} readOnly />
                        </div>
                        <div className='col-6'>
                          <label className='form-label small text-secondary mb-1'>External ID</label>
                          <input type='text' className='form-control' value={selectedContactData.externalId} readOnly />
                        </div>
                        <div className='col-3'>
                          <label className='form-label small text-secondary mb-1'>First name</label>
                          <input type='text' className='form-control' value={selectedContactData.firstName} readOnly />
                        </div>
                        <div className='col-3'>
                          <label className='form-label small text-secondary mb-1'>Last name</label>
                          <input type='text' className='form-control' value={selectedContactData.lastName} readOnly />
                        </div>
                        <div className='col-6'>
                          <label className='form-label small text-secondary mb-1'>E-mail address</label>
                          <input type='text' className='form-control' value={selectedContactData.email} readOnly />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              }
            />
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  )
}
